import { getComponent } from '../components/registry';
import { ComponentDef, ParamDef } from '../components/types';
import {
  hasDetailedRenderer,
  loadDetailedRenderer,
  hasRichRenderer,
  loadRichRenderer,
  StepInfo,
} from '../render/detailed';
import { GraphRenderer } from '../render/renderer';
import { createZoom, fitToView } from '../render/zoom';
import { navigateTo } from '../state';

export function renderComponentView(
  container: HTMLElement,
  id: string,
  query: Record<string, string>
): void {
  const component = getComponent(id);
  if (!component) {
    container.innerHTML = `
      <div class="not-found">
        <h1>Component not found</h1>
        <a href="#/">Back home</a>
      </div>
    `;
    return;
  }

  const params = resolveParams(component, query);
  const graph = component.buildGraph(params);

  if (hasRichRenderer(id)) {
    renderRichView(container, id, component, params, graph);
    return;
  }

  const wrapper = document.createElement('div');
  wrapper.className = 'component-view';

  wrapper.appendChild(createSidebar(component, params));
  wrapper.appendChild(createMainArea(id, component, params, graph));

  container.appendChild(wrapper);
}

// ── Rich 3-column view ──────────────────────────────────────────────────────

function renderRichView(
  container: HTMLElement,
  id: string,
  component: ComponentDef,
  params: Record<string, number | string>,
  graph: import('../graph/types').ComputationGraph
): void {
  const layout = document.createElement('div');
  layout.className = 'rich-view';

  // Left: step navigator
  const stepNav = document.createElement('aside');
  stepNav.className = 'rich-step-nav';

  const navHeader = document.createElement('div');
  navHeader.className = 'rich-nav-header';

  const navTitle = document.createElement('h2');
  navTitle.textContent = component.name;
  navHeader.appendChild(navTitle);

  const navDesc = document.createElement('p');
  navDesc.className = 'rich-nav-desc';
  navDesc.textContent = component.description;
  navHeader.appendChild(navDesc);

  stepNav.appendChild(navHeader);

  // Param form in left nav
  if (component.params.length > 0) {
    const paramSection = document.createElement('div');
    paramSection.className = 'rich-nav-params';
    const paramTitle = document.createElement('div');
    paramTitle.className = 'rich-params-title';
    paramTitle.textContent = 'Parameters';
    paramSection.appendChild(paramTitle);

    const form = document.createElement('form');
    form.className = 'param-form';
    form.addEventListener('submit', (e) => e.preventDefault());
    for (const param of component.params) {
      form.appendChild(createParamControl(param, params[param.name]));
    }
    paramSection.appendChild(form);
    stepNav.appendChild(paramSection);
  }

  // Steps list placeholder (populated after render)
  const stepsList = document.createElement('div');
  stepsList.className = 'rich-steps-list';
  const stepsTitle = document.createElement('div');
  stepsTitle.className = 'rich-params-title';
  stepsTitle.textContent = 'Steps';
  stepsList.appendChild(stepsTitle);
  stepNav.appendChild(stepsList);

  // Center: SVG diagram
  const diagramArea = document.createElement('main');
  diagramArea.className = 'rich-diagram';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'graph-svg');
  diagramArea.appendChild(svg);

  // Right: detail panel
  const detailPanel = document.createElement('aside');
  detailPanel.className = 'rich-detail-panel';
  detailPanel.innerHTML = `
    <div class="detail-placeholder">
      <div class="detail-placeholder-icon">→</div>
      <p>Select a step to see details</p>
    </div>
  `;

  layout.appendChild(stepNav);
  layout.appendChild(diagramArea);
  layout.appendChild(detailPanel);
  container.appendChild(layout);

  void renderRichDiagram(svg, id, component, params, graph, stepsList, detailPanel);
}

async function renderRichDiagram(
  svg: SVGSVGElement,
  id: string,
  component: ComponentDef,
  params: Record<string, number | string>,
  graph: import('../graph/types').ComputationGraph,
  stepsList: HTMLElement,
  detailPanel: HTMLElement
): Promise<void> {
  const renderFn = await loadRichRenderer(id);

  requestAnimationFrame(() => {
    const zoom = createZoom(svg);
    zoom.wrapper.selectAll('*').remove();
    const root = zoom.wrapper.append('g');
    const result = renderFn(root, params, component, graph);
    fitToView(zoom, result.width, result.height);

    const steps = result.steps as StepInfo[];
    populateStepNav(stepsList, detailPanel, steps, svg);
  });
}

function populateStepNav(
  stepsList: HTMLElement,
  detailPanel: HTMLElement,
  steps: StepInfo[],
  svg: SVGSVGElement
): void {
  steps.forEach((step, i) => {
    const item = document.createElement('button');
    item.className = 'rich-step-item';
    item.setAttribute('data-step', String(step.id));

    const badge = document.createElement('span');
    badge.className = 'rich-step-badge';
    badge.textContent = String(step.id);
    badge.style.background = step.color;

    const text = document.createElement('span');
    text.className = 'rich-step-label';
    text.textContent = step.shortLabel;

    item.appendChild(badge);
    item.appendChild(text);

    item.addEventListener('click', () => {
      // Update active state
      stepsList.querySelectorAll('.rich-step-item').forEach((el) =>
        el.classList.remove('active')
      );
      item.classList.add('active');

      // Highlight SVG nodes
      highlightStep(svg, step.svgGroup);

      // Render detail panel
      renderDetailPanel(detailPanel, step);
    });

    stepsList.appendChild(item);

    // Auto-activate first step
    if (i === 0) {
      item.classList.add('active');
      renderDetailPanel(detailPanel, step);
      requestAnimationFrame(() => highlightStep(svg, step.svgGroup));
    }
  });
}

function highlightStep(svg: SVGSVGElement, activeGroup: string): void {
  const allNodes = svg.querySelectorAll<SVGElement>('.step-node');
  allNodes.forEach((node) => {
    if (node.classList.contains(activeGroup)) {
      node.style.opacity = '1';
      node.style.filter = 'drop-shadow(0 0 6px rgba(0,0,0,0.18))';
    } else {
      node.style.opacity = '0.28';
      node.style.filter = 'none';
    }
  });
}

function renderDetailPanel(panel: HTMLElement, step: StepInfo): void {
  const codeLines = step.code.lines
    .map((line) => {
      if (!line) return '<span class="code-blank"></span>';
      return `<span class="code-line">${escapeHtml(line)}</span>`;
    })
    .join('\n');

  panel.innerHTML = `
    <div class="detail-content">
      <div class="detail-step-header" style="border-left: 4px solid ${step.color}">
        <span class="detail-step-num" style="background:${step.color}">Step ${step.id}</span>
        <h3 class="detail-step-title">${escapeHtml(step.label)}</h3>
      </div>

      <div class="detail-formula-block" style="border-color:${step.color}; background:${step.fill}">
        <span class="detail-formula-label">Formula</span>
        <code class="detail-formula">${escapeHtml(step.formula)}</code>
      </div>

      <div class="detail-explanation">
        <div class="detail-section-title">
          <span class="detail-icon insight-icon">💡</span>
          Explanation
        </div>
        <p class="detail-text">${escapeHtml(step.explanation)}</p>
      </div>

      <div class="detail-code-block">
        <div class="detail-code-header">
          <span class="detail-icon code-icon">⌨</span>
          <span>${escapeHtml(step.code.title)}</span>
        </div>
        <pre class="detail-code"><code>${codeLines}</code></pre>
      </div>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// ── Original 2-column helpers (unchanged) ──────────────────────────────────

function resolveParams(
  component: ComponentDef,
  query: Record<string, string>
): Record<string, number | string> {
  const result: Record<string, number | string> = {};
  for (const param of component.params) {
    const raw = query[param.name];
    if (raw !== undefined) {
      result[param.name] = param.type === 'number' ? Number(raw) : raw;
    } else {
      result[param.name] = param.default;
    }
  }
  return result;
}

function createSidebar(
  component: ComponentDef,
  params: Record<string, number | string>
): HTMLElement {
  const sidebar = document.createElement('aside');
  sidebar.className = 'sidebar';

  const title = document.createElement('h2');
  title.textContent = component.name;
  sidebar.appendChild(title);

  const desc = document.createElement('p');
  desc.className = 'description';
  desc.textContent = component.description;
  sidebar.appendChild(desc);

  const form = document.createElement('form');
  form.className = 'param-form';
  form.addEventListener('submit', (e) => e.preventDefault());

  for (const param of component.params) {
    form.appendChild(createParamControl(param, params[param.name]));
  }

  sidebar.appendChild(form);
  return sidebar;
}

function createParamControl(param: ParamDef, value: number | string): HTMLElement {
  const group = document.createElement('div');
  group.className = 'param-control';

  const label = document.createElement('label');
  label.textContent = param.label;
  label.htmlFor = `param-${param.name}`;
  group.appendChild(label);

  if (param.type === 'number') {
    const input = document.createElement('input');
    input.type = 'number';
    input.id = `param-${param.name}`;
    input.value = String(value);
    input.min = String(param.min ?? '');
    input.max = String(param.max ?? '');
    input.step = String(param.step ?? 'any');
    input.addEventListener('change', () => updateParam(param.name, Number(input.value)));
    group.appendChild(input);
  } else if (param.type === 'select' && param.options) {
    const select = document.createElement('select');
    select.id = `param-${param.name}`;
    for (const option of param.options) {
      const opt = document.createElement('option');
      opt.value = option.value;
      opt.textContent = option.label;
      if (option.value === value) opt.selected = true;
      select.appendChild(opt);
    }
    select.addEventListener('change', () => updateParam(param.name, select.value));
    group.appendChild(select);
  }

  return group;
}

function updateParam(name: string, value: number | string): void {
  const hash = window.location.hash;
  const [path, queryString = ''] = hash.split('?');
  const params = new URLSearchParams(queryString);
  params.set(name, String(value));
  navigateTo(`${path}?${params.toString()}`);
}

function createMainArea(
  id: string,
  component: ComponentDef,
  params: Record<string, number | string>,
  graph: import('../graph/types').ComputationGraph
): HTMLElement {
  const main = document.createElement('main');
  main.className = 'graph-stage';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'graph-svg');
  main.appendChild(svg);

  if (hasDetailedRenderer(id)) {
    void renderDetailedView(svg, id, component, params, graph);
  } else {
    requestAnimationFrame(() => {
      const renderer = new GraphRenderer(svg);
      renderer.render(graph);
    });
  }

  return main;
}

async function renderDetailedView(
  svg: SVGSVGElement,
  id: string,
  component: ComponentDef,
  params: Record<string, number | string>,
  graph: import('../graph/types').ComputationGraph
): Promise<void> {
  const renderFn = await loadDetailedRenderer(id);
  requestAnimationFrame(() => {
    const zoom = createZoom(svg);
    zoom.wrapper.selectAll('*').remove();
    const root = zoom.wrapper.append('g');
    const { width, height } = renderFn(root, params, component, graph);
    fitToView(zoom, width, height);
  });
}
